import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FieldArray, Form, Formik, useFormikContext } from "formik";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { AddIcon, CloseIcon, PlusIcon } from "../../assets/icons";
import { Badge, Button, Loading, PageTitle, Section } from "../../components";
import { TextInput } from "../../components/forms";

import { supplierValidationSchema } from "../../schemas/suppliers";
import useSupplierService from "../../services/api/supplierService";
import { enums } from "../../constants/enum";

import "./SuppliersPage.scss";

function EditSupplierPage() {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const {
    getSupplierById,
    queryKey,
    updateSupplier,
    addNewBankDetails,
    updateExistingBankDetails,
    deleteBankDetails,
  } = useSupplierService();
  const [loading, setLoading] = useState(false);
  const [removedBankAccountsNumbers, setRemovedBankAccountsNumbers] = useState(
    []
  );

  const queryClient = useQueryClient();

  const updateSupplierMutation = useMutation({
    mutationFn: updateSupplier,
  });

  const addBankDetailsMutation = useMutation({
    mutationFn: addNewBankDetails,
  });

  const updateBankDetailsMutation = useMutation({
    mutationFn: updateExistingBankDetails,
  });

  const deleteBankDetailsMutation = useMutation({
    mutationFn: deleteBankDetails,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: [queryKey, { supplierId }],
    queryFn: () => getSupplierById(supplierId),
    enabled: !!supplierId,
  });

  if (isLoading) return <Loading variant="fullScreen" />;
  if (error) {
    navigate("/suppliers");
    toast.error("Failed to load supplier details");
    return;
  }

  const initialValues = {
    name: data?.name || "",
    companyName: data?.companyName || "",
    phoneNumbers: data?.phoneNumbers || [""],
    bankDetails: data?.bankDetails || [
      {
        accountNumber: "",
        bankName: "",
        accountOwnerName: "",
        branch: "",
      },
    ],
  };
  const handleSubmit = async (values) => {
    setLoading(true);
    let isBasicDetailsChanged = false;
    // CHECK NAME, CONMPANY NAME OR MOBLIE NUMBERS HAD CHANGES
    if (values.name !== data.name) {
      isBasicDetailsChanged = true;
    }
    if (values.companyName !== data.companyName) {
      isBasicDetailsChanged = true;
    }

    if (values.phoneNumbers.length !== data.phoneNumbers.length) {
      isBasicDetailsChanged = true;
    }

    values.phoneNumbers.forEach((phoneNumber, index) => {
      if (phoneNumber !== data.phoneNumbers[index]) {
        isBasicDetailsChanged = true;
      }
    });

    if (isBasicDetailsChanged) {
      updateSupplierMutation.mutate(
        {
          id: supplierId,
          payload: {
            name: values.name,
            companyName: values.companyName,
            phoneNumbers: values.phoneNumbers,
          },
        },
        {
          onSuccess: (payload) => {
            console.log(payload);
            toast.success(payload?.message);
          },
          onError: (error) => {
            toast.error(
              error?.response?.data?.payload?.message ||
                enums.DEFAULT_ERROR_MESSAGE
            );
          },
        }
      );
    }

    // CHECK ACCOUNT DETAILS HAD CHANGED
    values.bankDetails
      .slice(0, data.bankDetails.length)
      .forEach((bankDetail, index) => {
        const original = data.bankDetails[index];
        if (
          bankDetail.accountNumber !== original.accountNumber ||
          bankDetail.bankName !== original.bankName ||
          bankDetail.accountOwnerName !== original.accountOwnerName ||
          bankDetail.branch !== original.branch
        ) {
          updateBankDetailsMutation.mutate(
            {
              id: supplierId,
              payload: {
                accountNumber: bankDetail.accountNumber,
                bankName: bankDetail.bankName,
                accountOwnerName: bankDetail.accountOwnerName,
                branch: bankDetail.branch,
              },
              bankDetailId: bankDetail._id,
            },
            {
              onSuccess: (payload) => {
                toast.success(payload?.message);
              },
              onError: (error) => {
                toast.error(
                  error?.response?.data?.payload?.message ||
                    enums.DEFAULT_ERROR_MESSAGE
                );
              },
            }
          );
        }
      });

    // CHECK ACCOUNT DETAILS HAD NEW ITEM
    if (values.bankDetails.length > data.bankDetails.length) {
      const newBankDetails = values.bankDetails.slice(data.bankDetails.length);
      addBankDetailsMutation.mutate(
        {
          id: supplierId,
          payload: { bankDetails: newBankDetails },
        },
        {
          onSuccess: (payload) => {
            toast.success(payload?.message);
          },
          onError: (error) => {
            toast.error(
              error?.response?.data?.payload?.message ||
                enums.DEFAULT_ERROR_MESSAGE
            );
          },
        }
      );
    }
    // CHECK ACCOUNT DETAILS HAD REMOVED
    if (removedBankAccountsNumbers.length > 0) {
      removedBankAccountsNumbers.forEach((accountNumber) => {
        deleteBankDetailsMutation.mutate(
          {
            id: supplierId,
            bankDetailId: accountNumber,
          },
          {
            onSuccess: (payload) => {
              toast.success(payload?.message);
            },
            onError: (error) => {
              toast.error(
                error?.response?.data?.payload?.message ||
                  enums.DEFAULT_ERROR_MESSAGE
              );
            },
          }
        );
      });
    }
    setLoading(false);
    await queryClient.resetQueries({
      queryKey: [queryKey, { supplierId }],
    });
    navigate(-1, { replace: true });
    // console.log(data);
  };
  return (
    <div className="page add-supplier-page">
      <PageTitle text="Edit Supplier Details" prev="/suppliers" />

      <Section className="supplier-form">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={supplierValidationSchema}
          onSubmit={handleSubmit}
        >
          <Form>
            <TextInput name="name" label="Name" placeholder="Type here" />
            <TextInput
              name="companyName"
              label="Company Name"
              placeholder="Type here"
            />
            {/* DYNAMIC INPUTS */}
            <MobileNumberInput />
            <BankDetailsInput
              setRemovedBankAccountsNumbers={setRemovedBankAccountsNumbers}
            />
            {/* END DYNAMIC INPUTS */}
            <Button
              text="Add New Supplier"
              type="submit"
              className="add-new-supplier"
              icon={<AddIcon />}
            />
          </Form>
        </Formik>
      </Section>

      <Loading isLoading={loading} variant="fullScreen" />
    </div>
  );
}

export default EditSupplierPage;

function MobileNumberInput() {
  const { values } = useFormikContext();
  return (
    <FieldArray name="phoneNumbers">
      {({ push, remove }) => (
        <>
          <div className="input-group">
            {values.phoneNumbers.map((phoneNumber, index) => (
              <div className="mobile-number-input-area" key={index}>
                <div className="label-area">
                  <Badge text={`Mobile Number ${index + 1}`} />

                  {index > 0 ? (
                    <Badge
                      icon={<CloseIcon className="icon" />}
                      onClick={() => remove(index)}
                    />
                  ) : null}
                </div>
                <div className="input-area">
                  <span>+94</span>
                  <TextInput
                    name={`phoneNumbers[${index}]`}
                    placeholder="Type here"
                    className="mobile-input-field"
                  />
                </div>
              </div>
            ))}
          </div>
          <Button
            text="Add Phone Number"
            type="button"
            icon={<PlusIcon />}
            className="add-new-number"
            onClick={() => push("")}
          />
        </>
      )}
    </FieldArray>
  );
}

function BankDetailsInput({ setRemovedBankAccountsNumbers }) {
  const { values } = useFormikContext();
  function handleRemoveBankAccount(accountNumber) {
    if (!accountNumber) {
      return;
    }
    setRemovedBankAccountsNumbers((prev) => [...prev, accountNumber]);
  }
  return (
    <FieldArray name="bankDetails">
      {({ push, remove }) => (
        <>
          {values.bankDetails.map((bankDetail, index) => (
            <Section wrapperClassName="supplier-bank-details" key={index}>
              <div className="supplier-bank-details-title">
                <Badge text={`Bank Account ${index + 1}`} />

                <Badge
                  icon={<CloseIcon className="icon" />}
                  onClick={() => {
                    remove(index);
                    handleRemoveBankAccount(bankDetail?._id);
                  }}
                />
              </div>
              <div className="input-group">
                <TextInput
                  name={`bankDetails[${index}].accountNumber`}
                  label="Account Number"
                  placeholder="Type here"
                />
                <TextInput
                  name={`bankDetails[${index}].bankName`}
                  label="Bank Name"
                  placeholder="Type here"
                />
              </div>
              <div className="input-group">
                <TextInput
                  name={`bankDetails[${index}].accountOwnerName`}
                  label="Account Name"
                  placeholder="Type here"
                />
                <TextInput
                  name={`bankDetails[${index}].branch`}
                  label="Branch"
                  placeholder="Type here"
                />
              </div>
            </Section>
          ))}
          <Button
            text="Add Bank Account"
            type="button"
            icon={<PlusIcon />}
            className="add-new-number"
            onClick={() =>
              push({
                accountNumber: "",
                bankName: "",
                accountOwnerName: "",
                branch: "",
              })
            }
          />
        </>
      )}
    </FieldArray>
  );
}
